import React, { useEffect, useState, useContext } from "react";
import {
  Spinner,
  Alert,
  Container,
  Card,
  Button,
  Modal,
  Form,
} from "react-bootstrap";
import { InputGroup, InputGroupText, Input, FormGroup } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { hasPermission, PERMISSIONS } from "utils/permissions";
import HusariaHeader from "views/pages/components/HusariaHeader";
import paginationFactory from "react-bootstrap-table2-paginator";
import BootstrapTable from "react-bootstrap-table-next";

const Timeconditions = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const userData = React.useMemo(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  }, []);
  const canEdit = hasPermission(userData, PERMISSIONS.EDIT);
    const canPerformActions =
      hasPermission(userData, PERMISSIONS.CREATE) ||
      hasPermission(userData, PERMISSIONS.EDIT) ||
      hasPermission(userData, PERMISSIONS.DELETE);
  const visibleColumns = [
    { key: "dialplan_name", label: "Name" },
    { key: "dialplan_number", label: "Number" },
    { key: "dialplan_context", label: "Context" },
    { key: "dialplan_order", label: "Order" },
    { key: "dialplan_enabled", label: "Enabled" },
    { key: "dialplan_description", label: "Description" },
  ];
  const tableName = "v_dialplans";
  const title = "Time Conditions";
  const parentName = "HUCS";
  const addRoute = "/admin/hucs/add/time-condition";
  const rowKey = "dialplan_uuid";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [search, setSearchTerm] = useState("");
  const [showAll, setShowAll] = useState(true);
  const navigate = useNavigate();

  const fetchRows = async () => {
    setLoading(true);
    try {
      const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
      const site_uuid = currentSite?.site_uuid || null;
      const response = await fetch(
        `${serverHost}/get_rows?table=${tableName}&pageSize=50&page=1&routeType=time_condition&domain_uuid=${encodeURIComponent(site_uuid)}`
      );
      if (!response.ok) throw new Error(`Failed to fetch ${tableName}`);
      const data = await response.json();
      setRows(data.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = visibleColumns.map((col, index) => ({
    dataField: col.key,
    text: col.label,
    sort: true,
    formatter: (cell, row) => {
      if (index === 0) {
        return canEdit ? (
          <a
            onClick={() => navigate(`/admin/hucs/edit/time-condition/${row[rowKey]}`)}
            style={{
              color: isDark ? "#60a5fa" : "#0d6efd",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            {cell ?? "-"}
          </a>
        ) : (
          <span style={{ fontWeight: 600 }}>{cell ?? "-"}</span>
        );
      }

      return cell ?? "-";
    },
  }));

  if (canPerformActions) {
    columns.push({
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) =>
        hasPermission(userData, PERMISSIONS.DELETE) ? (
          <Button
            size="sm"
            variant="danger"
            onClick={(e) => {toggleDeleteModal(row)
              e.stopPropagation()
            }}
          >
            Delete
          </Button>
        ) : null,
    });
  }

  const rowEvents = {
    onClick: (e, row, rowIndex) => {
      console.log(row[rowKey]);
      navigate(`/admin/hucs/edit/time-condition/${row[rowKey]}`);
    },
  };

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc", // "asc" | "desc"
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const filteredRows = React.useMemo(() => {
    if (!search && showAll) return rows;

    return rows.filter(
      (row) =>
        // Include only if showAll is true OR bridge_enabled is not "false"
        (showAll || row.bridge_enabled?.toLowerCase() !== "false") &&
        // AND matches search term
        visibleColumns.some((col) =>
          String(row[col.key] ?? "")
            .toLowerCase()
            .includes(search.toLowerCase())
        )
    );
  }, [rows, search, visibleColumns]);

  useEffect(() => {
    fetchRows();
  }, [tableName]);

  const toggleDeleteModal = (row = null) => {
    setRowToDelete(row);
    setDeleteModal(!deleteModal);
  };

  const handleDelete = async () => {
    if (!rowToDelete) return;
    const site = JSON.parse(localStorage.getItem("selectedSite"));
    try {
      const response = await fetch(
        `${serverHost}/delete_time_condition/${rowToDelete[rowKey]}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) throw new Error("Delete failed");
      await response.json();
      toggleDeleteModal();
      fetchRows();
    } catch (err) {
      alert(`Error deleting row: ${err.message}`);
    }
  };

  const singularTitle = title.endsWith("s") ? title.slice(0, -1) : title;

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading {title?.toLowerCase() || "data"}...
        </p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error}
      </Alert>
    );

  return (
    <>
      <HusariaHeader name={title} parentName={parentName} padding="4.5rem" />
      <Container fluid className="mt--6">
        <Card className={`shadow-sm ${isDark ? "bg-dark text-light" : ""}`}>
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
            className="d-flex justify-content-between align-items-center"
          >
            <div className="d-flex align-items-center">

              <Form className="navbar-search navbar-search-light form-inline search-border">
                <FormGroup className="mb-0">
                  <InputGroup className="input-group-alternative input-group-merge">
                    <InputGroupText>
                      <i
                        className="fas fa-search"
                        style={{
                          color: isDark ? "#b0b0b0" : "rgb(102, 102, 102)",
                        }}
                      />
                    </InputGroupText>
                    <Input
                      className="form-control table-search-bar"
                      placeholder="Search"
                      value={search}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: "220px",
                        borderTopRightRadius: "2rem",
                        borderBottomRightRadius: "2rem",
                        overflow: "hidden",
                      }}
                    />
                  </InputGroup>
                </FormGroup>
              </Form>
            </div>

            {/* Wrap buttons together */}
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <div style={{ paddingLeft: "1rem" }}>
                <Button variant="info" size="sm" onClick={fetchRows}>
                  Refresh
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(addRoute)}
                >
                  + Add {singularTitle}
                </Button>
              </div>
            </div>
          </Card.Header>

          <Card.Body
            style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}
          >
            <div>
              <div className="py-4 table-responsive">
                <BootstrapTable
                  keyField={rowKey}
                  data={filteredRows} // <-- use filteredRows instead of all rows
                  columns={columns}
                  bootstrap4
                  pagination={pagination}
                  bordered={false}
                  hover
                  rowEvents={rowEvents}
                  rowStyle={{ cursor: "pointer" }}
                  classes="align-items-center table-flush"
                  noDataIndication={() =>
                    loading ? (
                      <div className="text-center">Loading...</div>
                    ) : (
                      <div className="text-center">No records found.</div>
                    )
                  }
                />
              </div>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteModal} onHide={toggleDeleteModal}>
        <Modal.Header
          closeButton
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
          }}
        >
          <Modal.Title style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
            Delete {singularTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          Are you sure you want to delete this {singularTitle}? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: isDark ? "#111827" : "#f8f9fa",
          }}
        >
          <Button variant="light" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Timeconditions;
